import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { 
  ArrowRightIcon, 
  ArrowLeftIcon,
  EnvelopeIcon,
  CheckCircleIcon 
} from '@heroicons/react/24/outline';

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [emailSent, setEmailSent] = useState(false);

  // Твои премиальные стили Zootel
  const inputStyle = "w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-[14px] text-sm text-slate-900 placeholder:text-slate-400 focus:bg-white focus:border-[#F65814] focus:ring-4 focus:ring-[#F65814]/5 transition-all duration-200 outline-none font-medium";
  const labelStyle = "block text-[10px] font-black uppercase tracking-[1.5px] text-slate-400 mb-1.5 ml-1";
  const mainButtonStyle = "w-full bg-[#0A1128] text-white py-4 rounded-[18px] font-black uppercase tracking-[2px] text-[12px] flex items-center justify-center gap-2 hover:bg-[#152042] transition-all shadow-lg active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed";
  const secondaryButtonStyle = "w-full bg-white border border-slate-200 text-slate-600 py-3 rounded-[16px] font-bold text-[12px] uppercase tracking-wider flex items-center justify-center gap-2 hover:bg-slate-50 transition-all";

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) { setError('Please enter your email address'); return; }
    if (!isValidEmail(email)) { setError('Please enter a valid email address'); return; }

    setIsLoading(true);
    setError('');

    try {
      await axios.post('/api/auth/forgot-password', {
        email: email.trim().toLowerCase()
      });
      setEmailSent(true);
      setMessage('Instructions sent! Please check your inbox and spam folder.');
    } catch (err) {
      setError(err.response?.data?.message || 'Account not found or server error');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFDFD] flex items-center justify-center py-6 px-4 font-['Inter']">
      <motion.div 
        initial={{ opacity: 0, y: 10 }} 
        animate={{ opacity: 1, y: 0 }} 
        className="w-full max-w-[400px]"
      >
        <div className="text-center mb-8">
          <h2 className="text-3xl font-[900] text-[#0A1128] tracking-tight uppercase">
            Reset <span className="text-[#F65814]">Password</span>
          </h2>
          <p className="text-slate-500 text-[13px] mt-1 font-medium">
            We’ll send you a magic link to get back in
          </p>
        </div>

        <div className="bg-white p-6 md:p-8 rounded-[32px] shadow-[0_24px_48px_-12px_rgba(0,0,0,0.05)] border border-slate-100">
          <AnimatePresence mode="wait">
            {!emailSent ? (
              <motion.form 
                key="request-form"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 10 }}
                onSubmit={handleSubmit} 
                className="space-y-5"
              >
                {error && (
                  <div className="bg-red-50 text-[#F65814] text-[11px] font-bold p-3 rounded-[12px] border border-red-100 text-center uppercase tracking-wider">
                    {error}
                  </div>
                )}

                <div>
                  <label className={labelStyle}>Email Address</label>
                  <div className="relative">
                    <input
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className={inputStyle}
                      placeholder="name@mail.com"
                      disabled={isLoading}
                    />
                    <EnvelopeIcon className="absolute right-4 top-3.5 h-4 w-4 text-slate-300" />
                  </div>
                </div>

                <button type="submit" disabled={isLoading || !email} className={mainButtonStyle}>
                  {isLoading ? "Sending..." : "Send Instructions"}
                  {!isLoading && <ArrowRightIcon className="h-4 w-4" />}
                </button>

                <div className="text-center pt-2">
                  <Link to="/login" className="text-[10px] font-black text-[#0A1128] uppercase tracking-[1.5px] hover:text-[#F65814] transition-colors flex items-center justify-center gap-2">
                    <ArrowLeftIcon className="h-3 w-3" /> Back to Login
                  </Link>
                </div>
              </motion.form>
            ) : (
              <motion.div 
                key="success-message"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircleIcon className="h-8 w-8 text-green-500" />
                </div>
                <h3 className="text-[18px] font-black text-[#0A1128] uppercase tracking-tight mb-2">Check your inbox</h3>
                <p className="text-slate-500 text-[13px] font-medium mb-8">
                  {message}
                </p>
                
                <div className="space-y-3">
                  <Link to="/login" className={mainButtonStyle}>
                    Return to Login
                  </Link>
                  <button 
                    onClick={() => setEmailSent(false)} 
                    className={secondaryButtonStyle}
                  >
                    Use different email
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <div className="mt-8 text-center">
          <p className="text-[12px] text-slate-400 font-medium">
            Don't have an account?{' '}
            <Link to="/register" className="text-[#F65814] font-bold hover:underline underline-offset-4">
              Join Zootel
            </Link>
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default ForgotPasswordPage;